import React, { useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ScenarioRunClient, ScenarioRunList } from '../../api/api';
import { useAxiosSwagger } from '../../hooks/useAxiosSwagger';
import { CockpitEnvironment, EnvKind } from '../data/cockpit';
import { envKind, envSummary, envTone, useCockpit } from '../data/CockpitContext';
import { ago, clock, number, shortBuild } from '../data/format';
import { usePoll } from '../data/usePoll';
import { Card, EnvChip, Kv, Lamp, Meter, OwnerChip, Spinner } from '../components/ui';
import { v2 } from '../paths';

type Filter = 'all' | EnvKind;

const exactPct = (e: CockpitEnvironment) =>
    e.exactLimit && e.exactRemaining !== null && e.exactRemaining !== undefined ? ((e.exactLimit - e.exactRemaining) / e.exactLimit) * 100 : 0;

/** The lab card: what c2e test runs now, and as which customer. */
const LabCard = ({ lab }: { lab: CockpitEnvironment }) => {
    const axios = useAxiosSwagger();
    const runs = usePoll<ScenarioRunList>(
        axios ? () => new ScenarioRunClient(undefined, axios).scenarioRuns(lab.customer, lab.environment, 1) : null,
        10000,
        [axios, lab.customer, lab.environment]
    );
    const run = runs.data?.runs?.[0];
    const running = run && !run.finishedAtUtc && !run.stale;
    const totals = run?.totals;
    const done = (totals?.passed ?? 0) + (totals?.failed ?? 0) + (totals?.skipped ?? 0);
    const pct = totals?.total ? (done / totals.total) * 100 : 0;

    return (
        <section className="card" style={{ borderColor: '#1F5F58', background: '#0B1716' }}>
            <div className="ch" style={{ borderBottomColor: '#163B37' }}>
                <span className="ct">
                    <EnvChip kind="lab" environment={`${lab.customer} ${lab.environment}`} />
                    Scenario's
                </span>
                <span className="hrow">
                    {running ? (
                        <span className="chip info">
                            <Spinner />
                            Run loopt
                        </span>
                    ) : (
                        <span className="chip">Geen run bezig</span>
                    )}
                    {run?.runId && (
                        <Link className="btn sm" to={v2.run(lab.customer, lab.environment, run.runId)}>
                            {running ? 'Volgen' : 'Laatste run'}
                        </Link>
                    )}
                    <Link className="btn sm ghost" to={v2.scenarios()}>Scenario's</Link>
                </span>
            </div>
            <div className="cb g3">
                <Kv rows={[['Suite', run?.suiteName ?? '–'], ['Run', <span className="mono">{run?.runId ?? '–'}</span>]]} />
                <Kv rows={[['Build', <span className="mono">{shortBuild(run?.buildVersion) || '–'}</span>], ['Gestart', run?.startedAtUtc ? `${ago(run.startedAtUtc)} geleden` : '–']]} />
                <div>
                    <div className="hrow mono" style={{ justifyContent: 'space-between', fontSize: 12 }}>
                        <span>
                            {done} van {totals?.total ?? 0}
                        </span>
                        <span className="mut">{Math.round(pct)}%</span>
                    </div>
                    <Meter pct={pct} tone={(totals?.failed ?? 0) > 0 ? 'r' : 'i'} width="100%" />
                    <div className="hrow" style={{ fontSize: 12, marginTop: 8 }}>
                        <span className="ok">● {totals?.passed ?? 0} groen</span>
                        <span className={(totals?.failed ?? 0) > 0 ? 'bad' : 'mut'}>● {totals?.failed ?? 0} rood</span>
                        <span className="mut">● {(totals?.pending ?? 0) + (totals?.running ?? 0)} te gaan</span>
                    </div>
                </div>
            </div>
        </section>
    );
};

export const EnvironmentsPage = () => {
    const { environments, lab, loadedAt } = useCockpit();
    const navigate = useNavigate();
    const [filter, setFilter] = useState<Filter>('all');
    const [q, setQ] = useState('');
    const [tone, setTone] = useState<'ok' | 'warn' | 'bad' | null>(null);

    const rows = useMemo(() => {
        const toneRank = { bad: 0, warn: 1, ok: 2, off: 3 };
        return environments
            .filter((e) => filter === 'all' || envKind(e) === filter)
            .filter((e) => !q || e.customer.toLowerCase().includes(q.toLowerCase()))
            .filter((e) => !tone || envTone(e) === tone)
            .sort((a, b) => toneRank[envTone(a)] - toneRank[envTone(b)] || a.customer.localeCompare(b.customer));
    }, [environments, filter, q, tone]);

    const count = (t: string) => environments.filter((e) => envTone(e) === t).length;
    const behind = environments.filter((e) => e.behind).length;

    return (
        <>
            <div className="pagehead">
                <div>
                    <div className="eyebrow">
                        {new Set(environments.map((e) => e.customer)).size} klanten · {environments.length} omgevingen · bijgewerkt {loadedAt ? clock(loadedAt) : '–'}
                    </div>
                    <h1 className="h1">Omgevingen</h1>
                </div>
                <div className="hrow">
                    <label className="search" style={{ minWidth: 200 }}>
                        <input aria-label="Filter op klant" placeholder="Filter op klant" value={q} onChange={(e) => setQ(e.target.value)} />
                    </label>
                    <div className="seg" role="group" aria-label="Soort">
                        {(['all', 'prod', 'test', 'lab'] as Filter[]).map((k) => (
                            <button key={k} className={filter === k ? 'on' : ''} onClick={() => setFilter(k)}>
                                {{ all: 'Alles', prod: 'Productie', test: 'Test', lab: 'Lab' }[k]} · {environments.filter((e) => k === 'all' || envKind(e) === k).length}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            <div className="tiles">
                {([
                    { k: 'ok', label: 'Rustig', sub: 'niets te doen', v: '', cls: '' },
                    { k: 'warn', label: 'Let op', sub: 'wacht, remt, poison op test', v: 'warn', cls: 'cu' },
                    { k: 'bad', label: 'Actie nodig', sub: 'wij aan zet of poison op prod', v: 'bad', cls: 'us' },
                ] as const).map((t) => (
                    <button key={t.k} className={`card tile ${tone === t.k ? t.cls || 'on' : ''}`} style={tone === t.k ? { borderColor: '#5CC8FF' } : undefined} onClick={() => setTone(tone === t.k ? null : t.k)}>
                        <span className="l"><Lamp tone={t.k} size={10} />{t.label}</span>
                        <span className={`v ${t.v}`}>{count(t.k)}</span>
                        <span className="s">{tone === t.k ? 'gefilterd · klik om alles te zien' : t.sub}</span>
                    </button>
                ))}
                <div className="card tile"><span className="l">Achter op nieuwste build</span><span className="v">{behind}</span><span className="s">omgevingen</span></div>
            </div>

            {lab && <LabCard lab={lab} />}

            <Card>
                <div className="tw">
                    <table className="tbl">
                        <thead>
                            <tr>
                                <th>Klant</th>
                                <th>Omgeving</th>
                                <th>Stand</th>
                                <th className="num">Queue heen</th>
                                <th className="num">Poison</th>
                                <th>Exact vandaag</th>
                                <th>Build</th>
                                <th>Issues</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rows.map((e) => {
                                const tone = envTone(e);
                                return (
                                    <tr key={`${e.customer}/${e.environment}`} className={`hov ${tone === 'bad' ? 'bad' : tone === 'warn' ? 'dev' : ''}`} onClick={() => navigate(v2.env(e.customer, e.environment))}>
                                        <td><b>{e.customer}</b></td>
                                        <td><EnvChip kind={envKind(e)} environment={e.environment} /></td>
                                        <td><span className={`chip ${tone === 'off' ? '' : tone}`}>{envSummary(e)}</span></td>
                                        <td className="num">{number(e.queueChargebee)}</td>
                                        <td className={`num ${(e.poison ?? 0) > 0 ? (envKind(e) === 'prod' ? 'bad' : 'warn') : ''}`}>{number(e.poison)}</td>
                                        <td>
                                            <div className="hrow" style={{ gap: 8, flexWrap: 'nowrap' }}>
                                                <Meter pct={exactPct(e)} tone={e.exactRunsOutAt ? 'r' : exactPct(e) > 55 ? 'w' : ''} width={90} />
                                                <span className="mono mut" style={{ fontSize: 12 }}>
                                                    {number(e.exactRemaining)}
                                                    {e.exactRunsOutAt ? ` · op ${clock(e.exactRunsOutAt)}` : ''}
                                                </span>
                                            </div>
                                        </td>
                                        <td>
                                            <span className="mono">{shortBuild(e.build)}</span>{' '}
                                            {e.behind && <span className="chip warn" style={{ padding: '0 6px' }}>achter</span>}
                                        </td>
                                        <td>
                                            <div className="hrow" style={{ gap: 4 }}>
                                                {e.waitingOnUs > 0 && <span className="own us">{e.waitingOnUs}</span>}
                                                {e.waitingOnCustomer > 0 && <span className="own cu">{e.waitingOnCustomer}</span>}
                                                {e.resolvesItself > 0 && <span className="own au">{e.resolvesItself}</span>}
                                                {e.open === 0 && <span className="mut">–</span>}
                                            </div>
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className="hrow mut" style={{ padding: '10px 16px', fontSize: 12, gap: 16, borderTop: '1px solid #1A212B' }}>
                    <span><OwnerChip owner="us" /> wij actie nodig</span>
                    <span><OwnerChip owner="cu" /> klant actie nodig</span>
                    <span><OwnerChip owner="au" /> vanzelf</span>
                </div>
            </Card>
        </>
    );
};
